import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, Observable, shareReplay, throwError} from 'rxjs';
import {DadosLocalizacaoDemanda, Demanda, DemandaResponse} from '@core-models/demanda';
import {CadastrarDemandaPayload, ReforcarDemandaPayload} from '../models/demanda';
import {environment} from '@environments/environment';
import {PageProps} from '../models/pagination';
import {AuthService} from './auth.service';
import {EnumResponse} from '@core/models/enum-response';

export interface Filter {
  field: string;
  value: any;
}

@Injectable({
  providedIn: 'root'
})
export class DemandaService {
  private readonly apiUrl = environment.api.uri;
  private coordenadasContornoSaoPaulo: Observable<{ points: any[] }[]> | null = null;

  constructor(private readonly http: HttpClient, private readonly authService: AuthService) {
  }

  cadastrarDemanda(demanda: CadastrarDemandaPayload, idRepresentacao: number | null = null): Observable<Demanda> {
    if (this.authService.isAuthenticatedGovBr()) {
      let payload: any = demanda;
      if (idRepresentacao) {
        payload = {...demanda, idRepresentacao};
      }
      return this.http.post<Demanda>(`${this.apiUrl}demanda/govbr`, payload);
    }
    return this.http.post<Demanda>(`${this.apiUrl}demanda`, demanda);
  }

  listarDemandas(
    page: number = 0,
    size: number = 10,
    sortField?: string | null,
    sortDirection?: string | null,
    filters: Filter[] = []
  ): Observable<PageProps<Demanda>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (sortField && (sortDirection === 'asc' || sortDirection === 'desc')) {
      params = params.append('sort', `${sortField},${sortDirection}`);
    }

    for (const filtro of filters) {
      if (filtro.value !== null && filtro.value !== undefined && String(filtro.value).trim() !== '') {
        params = params.set(filtro.field, String(filtro.value));
      }
    }

    return this.http.get<PageProps<Demanda>>(`${this.apiUrl}demanda`, {params});
  }

  buscarDemandaPorId(id: number): Observable<DemandaResponse> {
    return this.http.get<DemandaResponse>(`${this.apiUrl}demanda/${id}`);
  }

  listarSituacoesDemanda(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}demanda-situacao`);
  }

  listarTiposProcesso(): Observable<EnumResponse[]> {
    return this.http.get<EnumResponse[]>(`${this.apiUrl}demanda/tipo-processo`);
  }

  listarDemandasPorLocalizacao(latitude: number, longitude: number): Observable<Demanda[]> {
    const params = new HttpParams()
      .append('latitude', latitude.toString())
      .append('longitude', longitude.toString());
    return this.http.get<PageProps<Demanda>>(`${this.apiUrl}demanda/listar-demandas-localizacao`, {params}).pipe(
      map(resposta => resposta.content)
    );
  }

  listarHistoricoReforcosPorDemandaId(id: number): Observable<Demanda[]> {
    return this.http.get<Demanda[]>(`${this.apiUrl}demanda/${id}/reforcos`);
  }


  reforcarDemanda(id: number, payload: ReforcarDemandaPayload): Observable<Demanda> {
    const url = `${this.apiUrl}demanda/${id}/reforcar-demanda`;
    if (this.authService.isAuthenticatedGovBr()) {
      return this.http.post<Demanda>(`${url}-govbr`, payload);
    }
    return this.http.post<Demanda>(url, payload);
  }

  getContornoSaoPaulo(): Observable<{ points: any[] }[]> {
    if (this.coordenadasContornoSaoPaulo) {
      return this.coordenadasContornoSaoPaulo;
    }
    this.coordenadasContornoSaoPaulo = this.http
      .get<{ points: any[] }[]>(`${this.apiUrl}gis-shape/sao-paulo`)
      .pipe(
        shareReplay(1),
        catchError((error) => {
          this.coordenadasContornoSaoPaulo = null;
          return throwError(() => error);
        })
      );
    return this.coordenadasContornoSaoPaulo;
  }

  getDemandaMalha(latitude: number, longitude: number, idTipoObra: number): Observable<DadosLocalizacaoDemanda> {
    const params = new HttpParams()
      .set('latitude', latitude)
      .set('longitude', longitude)
      .set('idTipoObra', idTipoObra);
    return this.http.get<DadosLocalizacaoDemanda>(`${this.apiUrl}demanda/buscar-demanda-malha`, {params});
  }
}
